import { createServerFn } from "@tanstack/react-start";
import { requireAdmin } from "@/lib/auth/middleware";
import * as db from "@/lib/db.server";

// Public display columns only — npi, license_number, fax, claim_token are excluded.
const DISPLAY_COLS =
  "id, name, practice, specialty, city, state, license_state, listing_status, photos, profile_photo_url, display_photo_url, has_custom_photo, avatar_gender, gender";

/**
 * Look up providers by NPI / license / name / practice on the server using the
 * service-role (BYPASSRLS) connection. NPI and license are usable as search
 * inputs but are NOT returned to the client. Public endpoint (no auth).
 */
export const findProvidersByIdentifierFn = createServerFn({ method: "POST" })
  .inputValidator((input: { query: string; limit?: number }) => ({
    query: String(input?.query ?? "").slice(0, 120),
    limit: Math.min(Math.max(Number(input?.limit ?? 20) || 20, 1), 20),
  }))
  .handler(async ({ data }) => {
    const raw = data.query.trim();
    if (raw.length < 2) return { rows: [] as any[] };

    const baseWhere = "lifecycle_status = 'active' AND is_deleted = false";

    const digits = raw.replace(/[\s-]/g, "");
    if (/^\d{10}$/.test(digits)) {
      const rows = await db.query(
        `SELECT ${DISPLAY_COLS} FROM public.providers
          WHERE ${baseWhere} AND npi = $1
          LIMIT $2`,
        [digits, data.limit],
      );
      return { rows: rows ?? [] };
    }

    const token = raw.split(/\s|·|,/)[0];
    if (/^[A-Za-z]{0,4}\d{3,}[A-Za-z0-9-]*$/.test(token) && token.length >= 4 && token.length <= 20) {
      const rows = await db.query(
        `SELECT ${DISPLAY_COLS} FROM public.providers
          WHERE ${baseWhere} AND license_number ILIKE $1
          LIMIT $2`,
        [token, data.limit],
      );
      if ((rows ?? []).length > 0) return { rows: rows ?? [] };
    }

    const text = raw.replace(/[%,]/g, " ").slice(0, 80).trim();
    if (!text) return { rows: [] as any[] };
    const like = `%${text}%`;
    const rows = await db.query(
      `SELECT ${DISPLAY_COLS} FROM public.providers
        WHERE ${baseWhere} AND (name ILIKE $1 OR practice ILIKE $1)
        LIMIT $2`,
      [like, data.limit],
    );
    return { rows: rows ?? [] };
  });

/**
 * Admin listings page data — runs as service_role after verifying the caller
 * has the 'admin' role (via requireAdmin). Returns NPI, license, fax for the
 * admin UI.
 */
const ADMIN_COLS =
  "id, npi, practice, name, address, city, state, phone, fax, email, license_number, listing_status, plan, is_featured, is_active, lifecycle_status, photo_review_status, created_at";

export const adminListProvidersFn = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .inputValidator((input: {
    cursor?: { created_at: string; id: string } | null;
    status?: string;
    aiFlag?: string;
    filters?: Partial<Record<"npi" | "practice" | "address" | "phone" | "fax" | "email" | "license", string>>;
    pageSize?: number;
  }) => ({
    cursor: input?.cursor ?? null,
    status: String(input?.status ?? "all"),
    aiFlag: String(input?.aiFlag ?? "all"),
    filters: input?.filters ?? {},
    pageSize: Math.min(Math.max(Number(input?.pageSize ?? 50) || 50, 1), 200),
  }))
  .handler(async ({ data }) => {
    const conditions: string[] = ["is_deleted = false"];
    const params: unknown[] = [];

    // Keyset cursor: rows strictly "after" (older than) the cursor in
    // (created_at DESC, id DESC) order.
    if (data.cursor) {
      params.push(data.cursor.created_at);
      const createdAtParam = `$${params.length}`;
      params.push(data.cursor.id);
      const idParam = `$${params.length}`;
      conditions.push(
        `(created_at < ${createdAtParam} OR (created_at = ${createdAtParam} AND id < ${idParam}))`,
      );
    }

    const s = data.status;
    if (s === "featured") {
      conditions.push("is_featured = true");
    } else if (s === "premium") {
      params.push("premium_monthly");
      const p1 = `$${params.length}`;
      params.push("premium_yearly");
      const p2 = `$${params.length}`;
      conditions.push(`plan IN (${p1}, ${p2})`);
    } else if (s === "free") {
      params.push("claimed");
      const c1 = `$${params.length}`;
      params.push("claimed_free");
      const c2 = `$${params.length}`;
      params.push("free");
      const planP = `$${params.length}`;
      conditions.push(`listing_status IN (${c1}, ${c2}) AND plan = ${planP}`);
    } else if (s === "lifetime") {
      params.push("lifetime_premium");
      conditions.push(`plan = $${params.length}`);
    } else if (s === "unclaimed") {
      params.push("unclaimed");
      conditions.push(`listing_status = $${params.length}`);
    }

    if (data.aiFlag !== "all") {
      params.push(data.aiFlag);
      conditions.push(`photo_review_status = $${params.length}`);
    }

    const colMap: Record<string, string> = {
      npi: "npi",
      practice: "practice",
      address: "address",
      phone: "phone",
      fax: "fax",
      email: "email",
      license: "license_number",
    };
    for (const [k, col] of Object.entries(colMap)) {
      const rawVal = (data.filters as any)[k];
      if (!rawVal) continue;
      const esc = String(rawVal).replace(/[%,()]/g, " ").trim();
      if (!esc) continue;
      const pattern = k === "npi" || k === "license" ? `${esc}%` : `%${esc}%`;
      params.push(pattern);
      conditions.push(`${col} ILIKE $${params.length}`);
    }

    const whereClause = conditions.length ? `WHERE ${conditions.join(" AND ")}` : "";
    // +1 to detect hasMore
    params.push(data.pageSize + 1);
    const limitParam = `$${params.length}`;

    const fetched = await db.query(
      `SELECT ${ADMIN_COLS}
         FROM public.providers
         ${whereClause}
        ORDER BY created_at DESC, id DESC
        LIMIT ${limitParam}`,
      params,
    );

    const more = fetched.length > data.pageSize;
    return { rows: more ? fetched.slice(0, data.pageSize) : fetched, hasMore: more };
  });

/**
 * Admin delete — runs as service_role after verifying admin role (requireAdmin).
 */
export const adminDeleteProviderFn = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .inputValidator((input: { id: string }) => ({ id: String(input.id) }))
  .handler(async ({ data }) => {
    await db.execute("DELETE FROM public.providers WHERE id = $1", [data.id]);
    return { ok: true };
  });
